<template>
  <div class="make-con" :class="{ open: isExpanded }">
    <div class="head-wrap">
      <h1 class="tit">{{ t(`label_${type}`) }} {{ t('create_new_chat') }}</h1>
      <button type="button" class="fold-btn" @click="isExpanded = !isExpanded">
        <IconArrow></IconArrow>
      </button>
    </div>

    <div class="opt-sel-con">
      <label class="lang-sel" @click="openSourceLanguagePopup">
        <input type="text" :placeholder="languagePlaceholder" disabled />
        <div class="my-sel-lang">
          <p>{{ sourceLanguage?.name ?? t('select_language') }}</p>
          <IconArrow></IconArrow>
        </div>
      </label>

      <label class="lang-sel" v-if="CHAT_KIND.isSingle(type)" @click="openTargetLanguagePopup">
        <input type="text" :placeholder="`${t('interpret_selected_language')}`" disabled />
        <div class="my-sel-lang">
          <p>{{ targetLanguage?.name ?? t('select_language') }}</p>
          <IconArrow></IconArrow>
        </div>
      </label>

      <div class="opt-sel-wrap">
        <div class="sel-item" v-if="CHAT_KIND.isOneOf(type, [CHAT_KIND.CONFERENCE, CHAT_KIND.GENERAL])">
          <span>{{ t('label_max_participants') }} {{ `(${t('not_allow_duplicated')})` }}</span>
          <RadioBox v-model="form.maxParticipants" :options="participantOptions" />
        </div>

        <div class="sel-item" v-if="CHAT_KIND.isOneOf(type, [CHAT_KIND.CONFERENCE, CHAT_KIND.MEETING])">
          <span>{{ t('label_screen_options') }}</span>
          <div class="app-ck-wrap">
            <CheckboxButton v-model="form.options.screenMode">
              {{ t('screen_view') }}
            </CheckboxButton>
            <CheckboxButton v-model="form.options.subtitleMode">
              {{ t('subtitle_view') }}
            </CheckboxButton>
          </div>
        </div>

        <div
          class="sel-item"
          v-if="CHAT_KIND.isOneOf(type, [CHAT_KIND.CONFERENCE, CHAT_KIND.MEETING, CHAT_KIND.GENERAL])"
        >
          <span>{{ t('label_interpretation_chatroom_name') }}</span>
          <input
            class="bg-input app-t-input"
            type="text"
            maxlength="30"
            enterkeyhint="done"
            :placeholder="`${t('to_connect')}`"
            v-model="form.title"
            v-cursor-end
            @keydown.enter.prevent="(e) => (e.target as HTMLInputElement).blur()"
          />
          <div class="guide-t">
            <p></p>
            <p class="num-ct">{{ form.title.length }}/20</p>
          </div>
        </div>
      </div>

      <div class="credit-info">
        <p v-if="chatUnitPrice > 0">
          {{ formatNumberWithComma(chatUnitPrice) }} {{ t('credit') }}
          <span>/ {{ t('label_minute') }}</span>
        </p>
      </div>
    </div>
  </div>

  <div class="make-btn-wrap">
    <button class="primary-btn l-btn" @click="startChat" :disabled="isLoading">
      <span>{{ t('label_start_interpretation') }}</span>
    </button>
  </div>

  <AppDivider />

  <div class="make-list" v-if="CHAT_KIND.isOneOf(type, [CHAT_KIND.CONFERENCE, CHAT_KIND.MEETING, CHAT_KIND.GENERAL])">
    <h1 class="app-tit">{{ t(`label_${type}`) }} {{ t('ss_list_prev_calls') }}</h1>
    <InterpertationUsed :chat-list="chatList" @toggle-favorite="toggleFavorite" @refresh="fetchUserChatList" />
  </div>
</template>

<script setup lang="ts">
import { cloneDeep, isEmpty } from 'lodash';

import AppDivider from '@/components/domains/mobile/common/AppDivider.vue';
import BottomSheet from '@/components/domains/mobile/common/PopupBottomSheet.vue';
import PopupModal from '@/components/domains/mobile/common/PopupModal.vue';
import InterpertationUsed from '@/components/domains/mobile/InterpertationUsed.vue';
import PopupSelectLanguage from '@/components/domains/mobile/popup/PopupSelectLanguage.vue';
import IconArrow from '@/components/jh-app/icons/IconArrow.vue';
import CheckboxButton from '@/components/shared/base/CheckboxButton.vue';
import RadioBox from '@/components/shared/base/RadioBox.vue';
import { useChatList } from '@/composables/domains/mobile/useChatList';
import { useCreateChat } from '@/composables/domains/mobile/useCreateChat';
import { getLanguageByCode } from '@/static/languages';
import { usePopupStore } from '@/stores/popup';
import { useUserStore } from '@/stores/user';
import { Language, Nullable } from '@/types/common';
import { ChatQuery, ParticipantOption } from '@/types/components';
import {
  CHAT_KIND,
  CHAT_OPTION_PRESETS,
  CHAT_PRICE_KEY_MAP,
  DEFAULT_CHAT_CREATE_FORM,
  SERVICE_PRICE_MAP,
} from '@/utils/constants/chat';
import { formatNumberWithComma } from '@/utils/stringUtils.js';
import { validateName } from '@/utils/validators';

const props = withDefaults(
  defineProps<{
    type: string;
  }>(),
  {
    type: '',
  }
);

const { t } = useI18n();
const router = useRouter();

const popupStore = usePopupStore();
const userStore = useUserStore();

const { chatList, setFilter, fetchUserChatList, toggleFavorite } = useChatList();
const { createChat, enterChat, unitPriceList, fetchChatUnitPrice, getChatFormData } = useCreateChat();

const isLoading = ref<boolean>(false);
const isExpanded = ref(true);
const sourceLanguage = ref<Language | null>(null);
const targetLanguage = ref<Language | null>(null);
const form = ref(cloneDeep(DEFAULT_CHAT_CREATE_FORM));

const chatType = computed(() => props.type.toUpperCase());

const participantOptions = computed<ParticipantOption[]>(() => {
  return getParticipantOptions(form.value.id);
});

const chatUnitPrice = computed<number>(() => {
  const { id = '', maxParticipants = '' } = form.value;
  const item = unitPriceList.value.find(({ planId }) => planId === `${id}${maxParticipants}`);

  if (!item) return 0;

  let finalUnitPrice = item.prepaymentUnitPrice;

  if (isEmpty(item.options)) {
    return finalUnitPrice;
  }

  const { SCREEN = 0, SUBTITLE = 0 } = item.options;
  if (form.value.options.screenMode) {
    finalUnitPrice += SCREEN;
  }
  if (form.value.options.subtitleMode) {
    finalUnitPrice += SUBTITLE;
  }

  return finalUnitPrice;
});

const languagePlaceholder = computed(() => {
  return CHAT_KIND.isSingle(chatType.value) ? `${t('label_my_language')}` : `${t('interpret_selected_language')}`;
});

const openSourceLanguagePopup = () => openLanguagePopup(sourceLanguage);
const openTargetLanguagePopup = () => openLanguagePopup(targetLanguage);

const openLanguagePopup = (targetRef: Ref<Language | null>) => {
  const excludedLanguages =
    toRaw(targetRef) === toRaw(sourceLanguage) ? [targetLanguage.value?.code] : [sourceLanguage.value!.code];

  popupStore.open(BottomSheet, {
    title: t('change_language'),
    contentSlot: h(PopupSelectLanguage, {
      excludedLanguages,
    }),
    payload: targetRef.value,
    showCloseButton: true,
    confirmButton: {
      action: (language: Language) => {
        targetRef.value = language;
      },
    },
  });
};

const startChat = async () => {
  if (isLoading.value) {
    return;
  }

  const errorMessage = validateChatForm();
  if (errorMessage) {
    popupStore.openToast(t(errorMessage));
    return false;
  }

  const sourceCode = sourceLanguage.value!.code;

  isLoading.value = true;

  try {
    if (CHAT_KIND.isOneOf(chatType.value, [CHAT_KIND.SINGLE, CHAT_KIND.AUDIENCE])) {
      return await proceedToEnterChat(sourceCode);
    }

    return await proceedToCreateChat(sourceCode);
  } finally {
    isLoading.value = false;
  }
};

const validateChatForm = (): Nullable<string> => {
  if (!sourceLanguage.value) {
    return 'error_input_no_language';
  }

  if (CHAT_KIND.isSingle(chatType.value) && !targetLanguage.value) {
    return 'please_select_language_away';
  }

  if (CHAT_KIND.isOneOf(chatType.value, [CHAT_KIND.CONFERENCE, CHAT_KIND.MEETING, CHAT_KIND.GENERAL])) {
    return validateName(form.value.title);
  }

  return null;
};

const proceedToEnterChat = async (sourceLanguage) => {
  const targetCode = CHAT_KIND.isSingle(chatType.value) ? targetLanguage.value!.code : null;
  const result = await enterChat(chatType.value, sourceLanguage, targetCode);

  if (!result.success) {
    if (result.errorType && result.errorType === 'credit') {
      openNotEnoughCreditPopup();
      return;
    }

    popupStore.openToast(t(result.error));
    return;
  }

  return goToSelfServiceChat(result.query);
};

const proceedToCreateChat = async (sourceLanguage) => {
  const formData = getChatFormData(form);
  const result = await createChat(formData, sourceLanguage);

  if (!result.success) {
    if (result.errorType && result.errorType === 'credit') {
      openNotEnoughCreditPopup();
      return;
    }

    popupStore.openToast(t(result.error));
    return;
  }

  return goToSelfServiceChat(result.query);
};

const goToSelfServiceChat = (query: ChatQuery) => {
  router.push({
    path: `/ss/chat/${props.type}`,
    query,
  });
};

const openNotEnoughCreditPopup = () => {
  popupStore.open(PopupModal, {
    title: t('error_credit_empty'),
    subTitle: t('credit_recharge_web'),
    confirmButton: { text: t('confirm') },
  });
};

const setupChatForm = () => {
  const id = CHAT_PRICE_KEY_MAP[chatType.value];
  const participantOptions = getParticipantOptions(id);
  const defaultParticipants = participantOptions[0]?.value ?? 0;

  const chatOption = { ...CHAT_OPTION_PRESETS[chatType.value] };

  form.value = {
    ...form.value,
    id,
    callType: chatType.value,
    maxParticipants: defaultParticipants,
    options: chatOption,
  };
};

const getParticipantOptions = (id: string) => {
  return Object.values(SERVICE_PRICE_MAP)
    .filter((item) => item.prefix === id)
    .map((item) => ({
      label: `${formatNumberWithComma(item.limitAttendee)} ${t('label_person_count')}`,
      value: item.limitAttendee,
    }));
};

onBeforeMount(async () => {
  setupChatForm();
  sourceLanguage.value = getLanguageByCode(userStore.userInfo.languageCode);

  await fetchChatUnitPrice();
  await setFilter({ chatTypes: [chatType.value] });
});
</script>

<style scoped lang="scss">
@import '@/assets/sass/components/appmaking';
.make-btn-wrap {
  margin-top: 2rem;
}

.opt-sel-con {
  .lang-sel {
    &:nth-child(1) {
      margin-bottom: 1rem;
    }
  }
}

.credit-info {
  min-height: 21px;
}
</style>
